import { Avatar, Radio } from '@doopage/react-ui-kit';
import { t } from '@lingui/macro';
import { answerPaymentLink } from 'api/api';
import BaseModal from 'component/BaseComponent/BaseModal';
import { handleApi } from 'helper/helper';
import Button from 'MUI_Kit/components/CustomButtons/Button';
import React from 'react';
import { connect } from 'react-redux';
import Routes from 'router/routes';
import { handlePaymentLinkProgress, usePaymentChannelBanks } from './utils';

// Options the provider refuses (unsupported bank, already added...) stay listed with the
// reason, so the owner knows why an account is missing
const AccountOptions = ({ options, selectedId, disabled, onSelect }) => {
  const { data: banks } = usePaymentChannelBanks();

  if (options.length === 0)
    return <div className="opacity-60">{t`Không tìm thấy tài khoản ngân hàng nào.`}</div>;

  return (
    <div className="flex flex-col gap-[8px]">
      {options.map((option) => {
        const bank = banks.find((item) => item.code === option.bank_code);
        const shortName = bank?.short_name || option.bank_code || '?';
        const selectable = option.selectable && !disabled;
        const selected = selectedId === option.id;
        return (
          <div
            key={option.id}
            className={`flex items-center rounded-lg border px-[14px] py-[10px] ${
              selected ? 'border-primary bg-gray-50' : 'border-gray-200'
            } ${option.selectable ? 'cursor-pointer' : 'opacity-50'}`}
            onClick={selectable ? () => onSelect(option.id) : undefined}>
            <Avatar src={bank?.logo_url} alt={shortName} size={40} />
            <div className="flex-1 ml-[12px]">
              <div className="font-bold">
                {[shortName, option.account_number].filter(Boolean).join(' • ')}
              </div>
              {!!option.account_name && (
                <div className="text-sm opacity-80">{option.account_name}</div>
              )}
              {!!option.note && <div className="text-xs opacity-60 mt-[2px]">{option.note}</div>}
            </div>
            {option.selectable && <Radio checked={selected} disabled={disabled} />}
          </div>
        );
      })}
    </div>
  );
};

// The `select` step of a payment link session: pick which of the provider's accounts
// becomes the payment channel
class SelectPaymentLinkAccountModal extends BaseModal {
  constructor(props) {
    super(props);
    const { progress } = this.getDataFromRouter();
    this.state = {
      open: true,
      title: t`Chọn tài khoản nhận tiền`,
      progress,
      selectedId: null,
      submitting: false,
      error: null,
    };
    this.goPathWhenClose = Routes.channel.path;
    this.disableBackdropClick = true;
  }

  componentDidMount() {
    // Reached without a session (e.g. the page was reloaded): nothing to pick from
    if (!this.state.progress?.session) this.close();
  }

  submit = async () => {
    const { progress, selectedId } = this.state;
    if (!progress || !selectedId) return;
    this.setState({ submitting: true, error: null });
    const { success, data, error } = await handleApi(
      answerPaymentLink(progress.session, { account_id: selectedId }),
    );
    if (!success) {
      // Stay open: the owner can pick another account or try again
      this.setState({ submitting: false, error: error || t`Có lỗi xảy ra, vui lòng thử lại` });
      return;
    }
    this.close();
    handlePaymentLinkProgress(data, { history: this.props.history, dispatch: this.props.dispatch });
  };

  renderBody() {
    const { progress, selectedId, submitting, error } = this.state;
    const step = progress?.step;
    return (
      <div className="flex flex-col gap-[12px]">
        <div className="opacity-70">
          {step?.hint || t`Chọn tài khoản ngân hàng sẽ nhận tiền thanh toán từ khách hàng.`}
        </div>
        <AccountOptions
          options={step?.options || []}
          selectedId={selectedId}
          disabled={submitting}
          onSelect={(id) => this.setState({ selectedId: id })}
        />
        {!!error && <div className="text-danger">{String(error)}</div>}
      </div>
    );
  }

  renderFooter() {
    const { selectedId, submitting } = this.state;
    return (
      <Button loading={submitting} disabled={!selectedId} onClick={this.submit} color="success">
        {t`Hoàn tất`}
      </Button>
    );
  }
}

export default connect()(SelectPaymentLinkAccountModal);
